import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";
import { Checkbox } from "./ui/checkbox";
import { Users, Phone, Building, CreditCard, FileText, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Customer, customerApiService } from "../services/customerApi";

interface CustomerData {
  customerName: string;
  firmName: string;
  vendorCode: string;
  email: string;
  mobileNo: string;
  landLineNo: string;
  address: string;
  stateName: string;
  stateCode: string;
  country: string;
  bankName: string;
  bankAccountNo: string;
  ifscCode: string;
  gstNo: string;
  panNo: string;
  isActive: boolean;
}

interface CustomerInformationFormProps {
  customer?: Customer | null;
  isViewMode?: boolean;
  onClose?: () => void;
  onSave?: () => void;
}

export function CustomerInformationForm({ customer, isViewMode = false, onClose, onSave }: CustomerInformationFormProps) {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;

  const isViewRoute = pathname.includes('/view');
  const isEditRoute = pathname.includes('/edit');
  const resolvedViewMode = isViewRoute || (!isEditRoute && isViewMode);

  const [resolvedCustomer, setResolvedCustomer] = useState<Customer | null>(customer ?? null);
  const [customerLoading, setCustomerLoading] = useState(!!id);
  const [customerError, setCustomerError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CustomerData>({
    customerName: '',
    firmName: '',
    vendorCode: '',
    email: '',
    mobileNo: '',
    landLineNo: '',
    address: '',
    stateName: '',
    stateCode: '',
    country: '',
    bankName: '',
    bankAccountNo: '',
    ifscCode: '',
    gstNo: '',
    panNo: '',
    isActive: true
  });

  // Fetch customer by ID when on /:id/edit or /:id/view
  useEffect(() => {
    if (!id) {
      setResolvedCustomer(customer ?? null);
      setCustomerLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setCustomerLoading(true);
      setCustomerError(null);
      try {
        const response = await customerApiService.getCustomerById(id);
        if (!cancelled && response.success && response.data) {
          setResolvedCustomer(response.data);
        } else if (!cancelled) {
          setCustomerError('Failed to load customer');
          setResolvedCustomer(null);
        }
      } catch {
        if (!cancelled) {
          setCustomerError('Failed to load customer');
          setResolvedCustomer(null);
        }
      } finally {
        if (!cancelled) setCustomerLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, customer]);

  // Populate form data when resolvedCustomer changes
  useEffect(() => {
    const source = resolvedCustomer;
    if (source) {
      setFormData({
        customerName: source.customerName || '',
        firmName: source.firmName || '',
        vendorCode: source.vendorCode || '',
        email: source.email || '',
        mobileNo: source.mobileNo || '',
        landLineNo: source.landLineNo || '',
        address: source.address || '',
        stateName: source.stateName || '',
        stateCode: source.stateCode || '',
        country: (source as any).country || '',
        bankName: source.bankName || '',
        bankAccountNo: source.bankAccountNo || '',
        ifscCode: source.ifscCode || '',
        gstNo: source.gstNo || '',
        panNo: (source as any).pan_no ?? (source as any).panNo ?? '',
        isActive: source.isActive !== undefined ? source.isActive : true
      });
    } else {
      // Reset form when no customer (new customer mode)
      setFormData({
        customerName: '',
        firmName: '',
        vendorCode: '',
        email: '',
        mobileNo: '',
        landLineNo: '',
        address: '',
        stateName: '',
        stateCode: '',
        country: '',
        bankName: '',
        bankAccountNo: '',
        ifscCode: '',
        gstNo: '',
        panNo: '',
        isActive: true
      });
    }
  }, [resolvedCustomer]);

  // States from /sales/states (name + code)
  const [statesList, setStatesList] = useState<{ name: string; code: string }[]>([]);
  const [statesLoading, setStatesLoading] = useState(true);
  const [statesError, setStatesError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatesLoading(true);
      setStatesError(null);
      try {
        const list = await customerApiService.getStates();
        if (!cancelled) setStatesList(list ?? []);
      } catch {
        if (!cancelled) {
          setStatesList([]);
          setStatesError('Could not load states');
        }
      } finally {
        if (!cancelled) setStatesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleInputChange = (field: keyof CustomerData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    
    try {
      // Prepare customer data for API
      const customerData = {
        customerName: formData.customerName,
        firmName: formData.firmName,
        vendorCode: formData.vendorCode,
        email: formData.email,
        mobileNo: formData.mobileNo,
        address: formData.address,
        stateName: formData.stateName,
        stateCode: formData.stateCode,
        country: formData.country,
        gstNo: formData.gstNo,
        pan_no: formData.panNo,
        bankName: formData.bankName,
        bankAccountNo: formData.bankAccountNo,
        ifscCode: formData.ifscCode,
        landLineNo: formData.landLineNo,
        isActive: formData.isActive
      };

      const customerId = id ?? resolvedCustomer?.id;
      if (customerId != null) {
        await customerApiService.updateCustomer(String(customerId), customerData);
        toast.success('Customer updated successfully!');
      } else {
        // Create new customer
        await customerApiService.createCustomer(customerData);
        toast.success('Customer created successfully!');
      }
      
      onSave?.();
      navigate('/customers');
    } catch (error) {
      // console.error('Failed to save customer:', error);
      toast.error(isEditRoute || id ? 'Failed to update customer' : 'Failed to create customer');
    }
  };

  const handleCancel = () => {
    navigate('/customers');
  };

  return (
    <Card className="w-full border-0 shadow-lg bg-white/70 backdrop-blur-sm">
      <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <CardTitle className="text-xl text-gray-900">
              {resolvedViewMode ? 'View Customer' : resolvedCustomer ? 'Edit Customer' : 'Add New Customer'}
            </CardTitle>
            <CardDescription className="text-primary-100">
              {resolvedViewMode ? 'View customer details' : resolvedCustomer ? 'Update customer information' : 'Enter customer details, contact information, and business data'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {customerLoading && (
          <div className="flex items-center justify-center py-12 text-primary-600">
            <span>Loading customer...</span>
          </div>
        )}
        {customerError && !customerLoading && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            {customerError}
            <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => navigate('/customers')}>
              Back to list
            </Button>
          </div>
        )}
        {!customerLoading && !customerError && (
        <form onSubmit={handleSave} className="space-y-8">
          {/* Personal & Business Information Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-primary-600">
                <FileText className="h-5 w-5 text-primary-700" />
                <h3 className="text-lg font-semibold">Personal & Business Information</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="customerName">Customer Name *</Label>
                  <Input
                    id="customerName"
                    value={formData.customerName}
                    onChange={(e) => handleInputChange('customerName', e.target.value)}
                    placeholder="Enter customer name"
                    disabled={resolvedViewMode}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="firmName">Firm Name</Label>
                  <Input
                    id="firmName"
                    value={formData.firmName}
                    onChange={(e) => handleInputChange('firmName', e.target.value)}
                    placeholder="Enter firm name"
                    disabled={resolvedViewMode}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="vendorCode">Vendor Code</Label>
                  <Input
                    id="vendorCode"
                    value={formData.vendorCode}
                    onChange={(e) => handleInputChange('vendorCode', e.target.value)}
                    placeholder="Enter vendor code"
                    disabled={resolvedViewMode}
                  />
                </div>
                
                <div className="flex items-center space-x-2 pt-8">
                  <Checkbox
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) => handleInputChange('isActive', checked as boolean)}
                    disabled={resolvedViewMode}
                  />
                  <Label htmlFor="isActive" className="text-sm text-primary-600">
                    Is Active
                  </Label>
                </div>
              </div>
            </div>
          </div>

          <Separator className="bg-primary-200" />

          {/* Contact Information Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-primary-600">
                <FileText className="h-5 w-5 text-primary-700" />
                <h3 className="text-lg font-semibold">Contact Information</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter email address"
                    disabled={resolvedViewMode}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="mobileNo">Mobile Number</Label>
                  <Input
                    id="mobileNo"
                    value={formData.mobileNo}
                    onChange={(e) => handleInputChange('mobileNo', e.target.value)}
                    placeholder="Enter mobile number"
                    disabled={resolvedViewMode}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="landlineNumber">Landline Number</Label>
                  <Input
                    id="landlineNumber"
                    value={formData.landLineNo}
                    onChange={(e) => handleInputChange('landLineNo', e.target.value)}
                    placeholder="Enter landline number"
                    disabled={resolvedViewMode}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    placeholder="Enter country"
                    disabled={resolvedViewMode}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="stateName">State</Label>
                  <Select
                    value={formData.stateName}
                    onValueChange={(value) => {
                      const selected = statesList.find((s) => s.name === value);
                      if (selected) {
                        setFormData((prev) => ({ ...prev, stateName: selected.name, stateCode: selected.code }));
                      } else {
                        handleInputChange('stateName', value);
                      }
                    }}
                    disabled={resolvedViewMode || statesLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={statesLoading ? 'Loading...' : statesError ? 'States unavailable' : 'Select state'} />
                    </SelectTrigger>
                    <SelectContentWithSearch searchPlaceholder="Search states...">
                      {statesList.map((s) => (
                        <SelectItem key={s.code} value={s.name}>
                          {s.name} ({s.code})
                        </SelectItem>
                      ))}
                    </SelectContentWithSearch>
                  </Select>
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Enter complete address"
                    disabled={resolvedViewMode}
                  />
                </div>
              </div>
            </div>
          </div>

          <Separator className="bg-primary-200" />

          {/* Tax & Registration Information Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-primary-600">
                <FileText className="h-5 w-5 text-primary-700" />
                <h3 className="text-lg font-semibold">Tax & Registration Information</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gstNo">GST Number</Label>
                  <Input
                    id="gstNo"
                    value={formData.gstNo}
                    onChange={(e) => handleInputChange('gstNo', e.target.value)}
                    placeholder="Enter GST number"
                    disabled={resolvedViewMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="panNo">PAN Number</Label>
                  <Input
                    id="panNo"
                    value={formData.panNo}
                    onChange={(e) => handleInputChange('panNo', e.target.value)}
                    placeholder="Enter PAN number"
                    disabled={resolvedViewMode}
                  />
                </div>
              </div>
            </div>
          </div>

          <Separator className="bg-primary-200" />

          {/* Banking Information Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-primary-600">
                <FileText className="h-5 w-5 text-primary-700" />
                <h3 className="text-lg font-semibold">Banking Information</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bankName">Bank Name</Label>
                  <Input
                    id="bankName"
                    value={formData.bankName}
                    onChange={(e) => handleInputChange('bankName', e.target.value)}
                    placeholder="Enter bank name"
                    disabled={resolvedViewMode}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="bankAccountNo">Bank Account Number</Label>
                  <Input
                    id="bankAccountNo"
                    value={formData.bankAccountNo}
                    onChange={(e) => handleInputChange('bankAccountNo', e.target.value)}
                    placeholder="Enter account number"
                    disabled={resolvedViewMode}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="ifscCode">IFSC Code</Label>
                  <Input
                    id="ifscCode"
                    value={formData.ifscCode}
                    onChange={(e) => handleInputChange('ifscCode', e.target.value)}
                    placeholder="Enter IFSC code"
                    disabled={resolvedViewMode}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {!resolvedViewMode && (
            <div className="flex items-center justify-end gap-4 pt-6 border-t border-primary-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="border-primary-200 text-primary-600 hover:bg-primary-50"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-primary-600 hover:bg-primary-700"
              >
                Save Customer
              </Button>
            </div>
          )}

          {resolvedViewMode && (
            <div className="flex items-center justify-end gap-4 pt-6 border-t border-primary-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="border-primary-200 text-primary-600 hover:bg-primary-50"
              >
                Close
              </Button>
            </div>
          )}
        </form>
        )}
      </CardContent>
    </Card>
  );
}